import { useCallback, useMemo } from 'react';

import { AxiosResponse } from 'axios';

import { useSuspenseQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeCodeState } from '~/types';

import { TypeTimeLineItem } from './useGetTodayWorkTimeline';

export type TypeMonthlyTimelineResponse = {
  code: string;
  message: string;
  data: {
    year: string;
    month: string;
    dailyTimelines: {
      /** 근무 상태 */
      workdayStatus: TypeCodeState;
      /** 근무 유형 */
      workType: TypeCodeState;
      /** 지각 시간 */
      lateMinutes: number;
      workday: string;
      cluster: TypeCodeState;
      center: TypeCodeState;
      workPart: TypeCodeState;
      teamName: string;
      workShift: string;
      workdayTimeline: TypeTimeLineItem[];
    }[];
  };
};

/** @description 월별 근무이력 타임라인 조회 */
export const useGetMonthlyTimeline = ({ yearMonth }: { yearMonth: string }) => {
  const handleResponse = useCallback((response: AxiosResponse<TypeMonthlyTimelineResponse>) => {
    return response.data.data.dailyTimelines ?? [];
  }, []);

  const { data, status, refetch } = useSuspenseQuery({
    queryKey: ['monthly-timeline', yearMonth],
    queryFn: async () => {
      const endpoint = '/monthly-timeline';
      return await axiosInstance.get(endpoint, { params: { yearMonth } });
    },
    gcTime: 0,
    retry: 1,
    select: (response) => handleResponse(response),
  });

  /** 캘린더에 강조 표시 할 날짜 (근무 이력이 있는 날짜) */
  const highlightedDays = useMemo(() => {
    if (status !== 'success') return [];
    return data.map((curr) => curr?.workday);
  }, [data, status]);

  /** 해당 날짜의 타임라인 목록 반환: 하루에 여러번 근무할 수 있음 */
  const getWorkDayTimeLineByWorkDay = useCallback(
    (workDay: string) => {
      if (status !== 'success') return [];

      return data.filter((item) => item.workday === workDay);
    },
    [data, status]
  );

  return { data, refetch, highlightedDays, getWorkDayTimeLineByWorkDay };
};
